import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { analytics } from '@/common/analytics/analytics'
import { OnboardClientAccessSubmittedAnalytics } from '@/common/analytics/events/OnboardClientAccessSubmittedAnalytics'
import { OnboardingClientSubmittedAnalytics } from '@/common/analytics/events/OnboardingClientSubmittedAnalytics'
import { AccountType, type AddressDetails, type TradeRegistryAddressType, type UserAccount } from '@/common/types'
import type { PostalAddress } from '@six-invest/api-types/shared/postal-address'
import { normalizeToAddressDetails } from '@/common/utils/utils'
import { useFeatureFlags } from '@/common/featureFlags/featureFlags'
import { useCreateAccount, useFetchAccountDetail, useUpsertAccountParticipants } from '@/account/accountQueries'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import {
    ACCOUNT_PARTICIPANT_ACCESS_PROFILES,
    ACCOUNT_PARTICIPANT_CONFIRMATION_STATUSES,
    ACCOUNT_PARTICIPANT_ROLES,
    CONTRACT_NOTES_CLIENT_PREFERENCES,
} from '@/account/type'
import { buildCommunicationPreferences } from './buildCommunicationPreferences'
import { getDefaultAccountName } from '@/account/getDefaultAccountName'
import { APPLICATION_COPY } from '@/locales/en'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { APP_ROUTES } from '@/common/constants'
import useAccountSwitcher from '@/account/useAccountSwitcher'
import { getAccountConflictId } from '@/account/accountConflictError'
import {
    APPLICATION_FLOW_EVENTS,
    APPLICATION_FLOW_IDS,
    APPLICATION_FLOW_OUTCOME_IDS,
    APPLICATION_FLOW_STEP_IDS,
    type ApplicationFlowStepId,
} from '../../applicationFlowConfig'
import { executeResolvedApplicationFlowActionOutcome } from '../../applicationFlowOutcomeExecutor'
import { useApplicationFlowResolver, type ResolvedApplicationFlowAction } from '../../useApplicationFlowResolver'
import type { SurveyFormValues } from '@/user/survey/surveyTypes'
import type { AddAccountParticipantDraft } from './types'

type UseAddAccountModalControllerArgs = {
    open: boolean
    onClose: () => void
    initialAccountType?: AccountType
}

const createEmptyParticipantDraft = (): AddAccountParticipantDraft => ({
    salutation: '',
    accessProfile: ACCOUNT_PARTICIPANT_ACCESS_PROFILES.NONE,
    dateOfBirth: '',
    differentPostalAddress: false,
    email: '',
    mobile: '',
    expectedTradeAmount: '',
    expectedTradeFrequency: '',
    firstName: '',
    investmentPurpose: '',
    middleName: '',
    lastName: '',
    occupation: '',
    postal: undefined,
    receiveTradeConfirmations: true,
    residential: undefined,
    contractNotePreference: CONTRACT_NOTES_CLIENT_PREFERENCES.PRIMARY_EMAIL,
    tradeRegistryAddressType: undefined,
})

type StepSubmitHandler = () => boolean | Promise<boolean>

/**
 * Determines the trade registration address based on user selection
 * Priority: user selection > adviser address > client address
 */
function getTradeRegistrationAddress(
    selectedType: TradeRegistryAddressType | undefined,
    adviserAddress: PostalAddress | undefined,
    clientAddress: PostalAddress | undefined,
): AddressDetails | undefined {
    if (selectedType === 'adviser') {
        return normalizeToAddressDetails(adviserAddress)
    }

    if (selectedType === 'client') {
        return normalizeToAddressDetails(clientAddress)
    }

    return normalizeToAddressDetails(adviserAddress ?? clientAddress)
}

export function useAddAccountLogic({ open, onClose, initialAccountType }: UseAddAccountModalControllerArgs) {
    const { user } = useUserStore()
    const primaryAccount = usePrimaryAccount()
    const { data: primaryAccountDetail } = useFetchAccountDetail(primaryAccount?.accountId, { enabled: open })
    const createAccount = useCreateAccount()
    const upsertAccountParticipants = useUpsertAccountParticipants()
    const navigate = useNavigate()
    const switchAccount = useAccountSwitcher()
    const { showCompanyAccounts } = useFeatureFlags()
    const isPrimaryAdviserUserLive = primaryAccount?.accountType === AccountType.ADVISER
    const [currentStepId, setCurrentStepId] = useState<ApplicationFlowStepId>(
        APPLICATION_FLOW_STEP_IDS.SELECT_ACCOUNT_TYPE,
    )
    const [participants, setParticipants] = useState<AddAccountParticipantDraft[]>([createEmptyParticipantDraft()])
    const [preparedAdviserAccount, setPreparedAdviserAccount] = useState<UserAccount | null>(null)
    const [preparedAdviserParticipantId, setPreparedAdviserParticipantId] = useState<string | null>(null)
    const [selectedAccountType, setSelectedAccountType] = useState<AccountType | undefined>()
    const [isTrust, setIsTrust] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isPrimaryAdviserUserForFlow, setIsPrimaryAdviserUserForFlow] = useState(isPrimaryAdviserUserLive)
    const [createdAccountId, setCreatedAccountId] = useState<string | null>(null)
    const adviserPreparationPromiseRef = useRef<Promise<{
        createdAccount: UserAccount
        participantId: string
    } | null> | null>(null)
    const stepSubmitHandlersRef = useRef<Partial<Record<ApplicationFlowStepId, StepSubmitHandler>>>({})
    const previousOpenRef = useRef(false)
    const submitInFlightRef = useRef(false)

    const primaryParticipant = participants[0] ?? createEmptyParticipantDraft()

    const supportedAccountTypes = useMemo(() => {
        const types = [AccountType.INDIVIDUAL_TRUST, AccountType.COMPANY_TRUST]
        if (primaryAccount?.accountType !== AccountType.INDIVIDUAL) {
            types.push(AccountType.INDIVIDUAL)
        }
        if (showCompanyAccounts) {
            types.push(AccountType.COMPANY)
        }
        return types
    }, [primaryAccount?.accountType, showCompanyAccounts])

    const isPrimaryAdviserUser = isPrimaryAdviserUserForFlow

    const flowContext = useMemo(
        () => ({
            isPrimaryAdviserUser,
            participantAccessProfile: primaryParticipant.accessProfile,
            participantDateOfBirth: primaryParticipant.dateOfBirth,
            participantSalutation: primaryParticipant.salutation,
            participantEmail: primaryParticipant.email,
            participantMobile: primaryParticipant.mobile,
            participantFirstName: primaryParticipant.firstName,
            participantOccupation: primaryParticipant.occupation,
            participantInvestmentPurpose: primaryParticipant.investmentPurpose,
            participantExpectedTradeFrequency: primaryParticipant.expectedTradeFrequency,
            participantExpectedTradeAmount: primaryParticipant.expectedTradeAmount,
            participantHasDifferentPostalAddress: primaryParticipant.differentPostalAddress,
            participantLastName: primaryParticipant.lastName,
            participantPostalAddress: primaryParticipant.postal,
            participantResidentialAddress: primaryParticipant.residential,
            selectedAccountType,
        }),
        [
            isPrimaryAdviserUser,
            primaryParticipant.accessProfile,
            primaryParticipant.salutation,
            primaryParticipant.occupation,
            primaryParticipant.investmentPurpose,
            primaryParticipant.expectedTradeFrequency,
            primaryParticipant.expectedTradeAmount,
            primaryParticipant.dateOfBirth,
            primaryParticipant.differentPostalAddress,
            primaryParticipant.email,
            primaryParticipant.mobile,
            primaryParticipant.firstName,
            primaryParticipant.lastName,
            primaryParticipant.postal,
            primaryParticipant.residential,
            selectedAccountType,
        ],
    )

    const resolver = useApplicationFlowResolver({
        flowId: APPLICATION_FLOW_IDS.ADD_ACCOUNT_MODAL,
        context: flowContext,
        stepId: currentStepId,
    })

    const progressPercentage = resolver.step.ui.progressPercentage ?? 25
    const isAccountTypeStep = currentStepId === APPLICATION_FLOW_STEP_IDS.SELECT_ACCOUNT_TYPE

    const resetLocalState = useCallback(() => {
        setCurrentStepId(APPLICATION_FLOW_STEP_IDS.SELECT_ACCOUNT_TYPE)
        setIsSubmitting(false)
        setParticipants([createEmptyParticipantDraft()])
        setPreparedAdviserAccount(null)
        setPreparedAdviserParticipantId(null)
        setSelectedAccountType(undefined)
        setIsTrust(false)
        setIsPrimaryAdviserUserForFlow(isPrimaryAdviserUserLive)
        setCreatedAccountId(null)
        adviserPreparationPromiseRef.current = null
        stepSubmitHandlersRef.current = {}
        submitInFlightRef.current = false
    }, [isPrimaryAdviserUserLive])

    const updateParticipant = useCallback((index: number, updates: Partial<AddAccountParticipantDraft>) => {
        setParticipants((currentParticipants) => {
            const nextParticipants = [...currentParticipants]
            const existingParticipant = nextParticipants[index] ?? createEmptyParticipantDraft()

            nextParticipants[index] = {
                ...existingParticipant,
                ...updates,
            }

            return nextParticipants
        })
    }, [])

    const applyParticipantSurvey = useCallback(
        (index: number, survey: SurveyFormValues) => {
            updateParticipant(index, {
                occupation: survey.occupation,
                investmentPurpose: survey.investmentPurpose,
                expectedTradeFrequency: survey.expectedTradeFrequency,
                expectedTradeAmount: survey.expectedTradeAmount,
            })
        },
        [updateParticipant],
    )

    const registerStepSubmitHandler = useCallback((stepId: ApplicationFlowStepId, handler: StepSubmitHandler) => {
        stepSubmitHandlersRef.current[stepId] = handler

        return () => {
            if (stepSubmitHandlersRef.current[stepId] === handler) {
                delete stepSubmitHandlersRef.current[stepId]
            }
        }
    }, [])

    const handleModalClose = useCallback(() => {
        analytics.trackWithDefaults(
            isAccountTypeStep ? 'add_account_cancel_button_clicked' : 'add_account_confirmation_cancel_button_clicked',
        )

        resetLocalState()
        onClose()
    }, [isAccountTypeStep, onClose, resetLocalState])

    const syncCreatedAccountToStoreAndSwitch = useCallback(
        async (createdAccount: UserAccount) => {
            const store = useAccountStore.getState()
            const currentAccounts = store.getAccounts()
            const alreadyAdded = currentAccounts.find((account) => account.accountId === createdAccount.accountId)

            if (!alreadyAdded) {
                store.setAccounts([...currentAccounts, createdAccount])
            }

            const switched = await switchAccount(createdAccount.accountId)
            if (!switched) {
                throw new Error('Failed to switch to newly created account')
            }
        },
        [switchAccount],
    )

    const createAdviserAccountAndPrepareKyc = useCallback(() => {
        if (!selectedAccountType) {
            return Promise.resolve(null)
        }

        if (preparedAdviserAccount && preparedAdviserParticipantId) {
            return Promise.resolve({
                createdAccount: preparedAdviserAccount,
                participantId: preparedAdviserParticipantId,
            })
        }

        if (adviserPreparationPromiseRef.current) {
            return adviserPreparationPromiseRef.current
        }

        adviserPreparationPromiseRef.current = (async () => {
            try {
                const clientPostalAddress = primaryParticipant.differentPostalAddress
                    ? primaryParticipant.postal
                    : primaryParticipant.residential

                const tradeRegistryAddress = getTradeRegistrationAddress(
                    primaryParticipant.tradeRegistryAddressType,
                    primaryAccountDetail?.postalAddress,
                    clientPostalAddress,
                )

                const createdAccount = await createAccount.mutateAsync({
                    accountType: selectedAccountType,
                    accountName: getDefaultAccountName(selectedAccountType, {
                        firstName: primaryParticipant.firstName.trim(),
                        lastName: primaryParticipant.lastName.trim(),
                    }),
                    communicationPreferences: buildCommunicationPreferences(primaryParticipant),
                    postalAddress: tradeRegistryAddress,
                })

                await syncCreatedAccountToStoreAndSwitch(createdAccount)

                const upsertResponse = await upsertAccountParticipants.mutateAsync({
                    accountId: createdAccount.accountId,
                    participants: [
                        {
                            salutation: primaryParticipant.salutation?.trim() || undefined,
                            accessProfile: primaryParticipant.accessProfile ?? ACCOUNT_PARTICIPANT_ACCESS_PROFILES.NONE,
                            email: primaryParticipant.email.trim(),
                            mobile: primaryParticipant.mobile?.trim() || undefined,
                            firstName: primaryParticipant.firstName.trim(),
                            lastName: primaryParticipant.lastName.trim(),
                            middleName: primaryParticipant.middleName.trim() || undefined,
                            dateOfBirth: primaryParticipant.dateOfBirth || undefined,
                            occupation: primaryParticipant.occupation?.trim() || undefined,
                            investmentPurpose: primaryParticipant.investmentPurpose || undefined,
                            expectedTradeFrequency: primaryParticipant.expectedTradeFrequency || undefined,
                            expectedTradeAmount: primaryParticipant.expectedTradeAmount || undefined,
                            residentialAddress: primaryParticipant.residential,
                            postalAddress: primaryParticipant.differentPostalAddress
                                ? primaryParticipant.postal
                                : primaryParticipant.residential,
                            role: ACCOUNT_PARTICIPANT_ROLES.PRIMARY_APPLICANT,
                        },
                    ],
                })

                const participantResult =
                    upsertResponse.results.find(
                        (result) =>
                            result.status === ACCOUNT_PARTICIPANT_CONFIRMATION_STATUSES.REQUIRED ||
                            result.status === ACCOUNT_PARTICIPANT_CONFIRMATION_STATUSES.NOT_REQUIRED,
                    ) ?? upsertResponse.results[0]

                const participantId = participantResult?.participantId

                if (!participantId) {
                    throw new Error('Missing participant id for KYC init')
                }

                setPreparedAdviserAccount(createdAccount)
                setPreparedAdviserParticipantId(participantId)
                return { createdAccount, participantId }
            } catch (error) {
                adviserPreparationPromiseRef.current = null
                throw error
            }
        })()

        return adviserPreparationPromiseRef.current
    }, [
        createAccount,
        preparedAdviserAccount,
        preparedAdviserParticipantId,
        primaryAccountDetail?.postalAddress,
        primaryParticipant,
        selectedAccountType,
        syncCreatedAccountToStoreAndSwitch,
        upsertAccountParticipants,
    ])

    const submitAddAccount = useCallback(async () => {
        if (!selectedAccountType) return
        if (submitInFlightRef.current) return

        submitInFlightRef.current = true
        setIsSubmitting(true)

        OnboardingClientSubmittedAnalytics.track({
            account_type: selectedAccountType,
            access_level: primaryParticipant.accessProfile ?? ACCOUNT_PARTICIPANT_ACCESS_PROFILES.NONE,
        })

        try {
            if (isPrimaryAdviserUser) {
                const adviserPreparation = await createAdviserAccountAndPrepareKyc()
                if (!adviserPreparation) {
                    setIsSubmitting(false)
                    submitInFlightRef.current = false
                    return
                }
                navigate(APP_ROUTES.APPLICATION_KYC_CONFIRM, {
                    state: {
                        accountType: selectedAccountType,
                        createdAccountId: adviserPreparation.createdAccount.accountId,
                        participantId: adviserPreparation.participantId,
                        isAdviser: true,
                    },
                })
                return
            }

            const createdAccount = await createAccount.mutateAsync({
                accountType: selectedAccountType,
                accountName: getDefaultAccountName(selectedAccountType, user),
                communicationPreferences: buildCommunicationPreferences(primaryParticipant),
            })
            setCreatedAccountId(createdAccount.accountId)
            await syncCreatedAccountToStoreAndSwitch(createdAccount)
            navigate(APP_ROUTES.APPLICATION_KYC_CONFIRM, {
                state: {
                    accountType: selectedAccountType,
                    createdAccountId: createdAccount.accountId,
                    isAdviser: false,
                },
            })
        } catch (error) {
            const conflictAccountId = getAccountConflictId(error)
            if (conflictAccountId) {
                // switchAccount validates the target exists before resolving true, so a
                // stale/invalid conflictAccountId falls through to the generic failure below
                // instead of stranding the user on a broken dashboard.
                const switched = await switchAccount(conflictAccountId)
                if (switched) {
                    navigate(APP_ROUTES.DASHBOARD)
                    return
                }
            }

            analytics.trackWithDefaults('add_account_type_failed')
            toast.error(APPLICATION_COPY.confirmation.errorToast)
            setIsSubmitting(false)
            submitInFlightRef.current = false
        }
    }, [
        createAccount,
        createAdviserAccountAndPrepareKyc,
        isPrimaryAdviserUser,
        navigate,
        primaryParticipant,
        selectedAccountType,
        switchAccount,
        syncCreatedAccountToStoreAndSwitch,
        user,
    ])

    const handleResolvedAction = async (action?: ResolvedApplicationFlowAction) => {
        if (isSubmitting) return
        if (!action?.enabled || !action.transition) return

        if (action.event === APPLICATION_FLOW_EVENTS.CONTINUE) {
            const submitHandler = stepSubmitHandlersRef.current[currentStepId]
            if (submitHandler) {
                const shouldContinue = await submitHandler()
                if (!shouldContinue) return
            }

            if (currentStepId === APPLICATION_FLOW_STEP_IDS.PARTICIPANT_ACCESS_REVIEW) {
                OnboardClientAccessSubmittedAnalytics.track({
                    access_level: primaryParticipant.accessProfile ?? ACCOUNT_PARTICIPANT_ACCESS_PROFILES.NONE,
                })
            }
        }

        if (
            action.transition.target.kind === 'outcome' &&
            action.transition.target.outcome === APPLICATION_FLOW_OUTCOME_IDS.PROCEED_TO_KYC_CONFIRM
        ) {
            await submitAddAccount()
            return
        }

        if (action.transition.target.kind === 'step') {
            setCurrentStepId(action.transition.target.stepId)
            return
        }

        executeResolvedApplicationFlowActionOutcome(action, {
            [APPLICATION_FLOW_OUTCOME_IDS.CLOSE_MODAL]: handleModalClose,
        })
    }

    useEffect(() => {
        const redirect = resolver.redirect
        if (!redirect || isSubmitting) return

        if (redirect.target.kind === 'step') {
            if (redirect.target.stepId !== currentStepId) {
                setCurrentStepId(redirect.target.stepId)
            }
            return
        }

        if (
            redirect.target.kind === 'outcome' &&
            redirect.target.outcome === APPLICATION_FLOW_OUTCOME_IDS.PROCEED_TO_KYC_CONFIRM
        ) {
            void submitAddAccount()
            return
        }

        if (
            redirect.target.kind === 'outcome' &&
            redirect.target.outcome === APPLICATION_FLOW_OUTCOME_IDS.CLOSE_MODAL
        ) {
            handleModalClose()
        }
    }, [currentStepId, handleModalClose, isSubmitting, resolver.redirect, submitAddAccount])

    useEffect(() => {
        const wasOpen = previousOpenRef.current
        previousOpenRef.current = open

        if (!open) return
        if (wasOpen) return

        setIsPrimaryAdviserUserForFlow(isPrimaryAdviserUserLive)
        setIsSubmitting(false)
        submitInFlightRef.current = false

        if (!initialAccountType) {
            resetLocalState()
            return
        }

        setSelectedAccountType(initialAccountType)
        setIsTrust(
            initialAccountType === AccountType.INDIVIDUAL_TRUST || initialAccountType === AccountType.COMPANY_TRUST,
        )
        setCurrentStepId(
            isPrimaryAdviserUser
                ? APPLICATION_FLOW_STEP_IDS.PARTICIPANT_DETAILS
                : APPLICATION_FLOW_STEP_IDS.SELECT_ACCOUNT_TYPE,
        )
    }, [initialAccountType, isPrimaryAdviserUser, isPrimaryAdviserUserLive, open, resetLocalState])

    return {
        createdAccountId,
        handleModalClose,
        handleResolvedAction,
        isAccountTypeStep,
        isTrust,
        participants,
        primaryParticipant,
        primaryAccount,
        primaryAccountRegistrationAddress: primaryAccountDetail?.postalAddress,
        progressPercentage,
        resolver,
        isSubmitting,
        selectedAccountType,
        setCurrentStepId,
        setIsTrust,
        setSelectedAccountType,
        supportedAccountTypes,
        updateParticipant,
        applyParticipantSurvey,
        registerStepSubmitHandler,
        isPrimaryAdviserUser,
    }
}
